/* ============================================================================
   Wishlist Shopify 2.0
   ============================================================================ */

(() => {
  // --- Config -------------------------------------------------------------
  const _fetch = window.fetch ? window.fetch.bind(window) : null;
  const COOKIE_NAME = 'shop_wishlist';

  // --- Helpers cookie <→> Array ------------------------------------------
  const readCookie = (name) =>
    (document.cookie.split('; ').find(r => r.startsWith(name + '=')) || '')
      .split('=')[1] || '';

  const writeCookie = (name, value, days = 365) => {
    const exp = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${exp}; path=/; SameSite=Lax`;
  };

  const parse = (str) => {
    try { return JSON.parse(decodeURIComponent(str)) || []; }
    catch { return []; }
  };

  const stringify = (arr) => encodeURIComponent(JSON.stringify(arr));

  // --- Utils --------------------------------------------------------------
  // ID valide = chaîne de chiffres (id numérique du variant Shopify)
  const isValidVariantId = (v) => typeof v === 'string' && /^\d+$/.test(v);
  // Déduplique + filtre IDs valides
  const sanitizeIds = (ids) => [...new Set((ids || []).map(String).filter(isValidVariantId))];
  const mergeWishlists = (...lists) => sanitizeIds(lists.flat());
  const sameWishlist = (a, b) => {
    const left = sanitizeIds(a).slice().sort();
    const right = sanitizeIds(b).slice().sort();

    if (left.length !== right.length) return false;
    return left.every((id, index) => id === right[index]);
  };
  const getCustomerId = () =>
    (typeof window !== 'undefined' && window.__SHOP_CUSTOMER_ID__) || null;
  const getCustomerWishlist = () =>
    sanitizeIds((((typeof window !== 'undefined' && window.__SHOP_CUSTOMER_WISHLIST__) || []).map(String)));

  // Formatage prix (robuste et localisé)
  const fmtPrice = (amount, currency) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return [amount, currency].filter(Boolean).join(' ') || '';
    const cur = currency || 'EUR';
    const lang = document.documentElement.lang || 'fr-FR';
    try {
      return new Intl.NumberFormat(lang, { style: 'currency', currency: cur }).format(n);
    } catch {
      return `${n} ${cur}`;
    }
  };

  // --- État local ---------------------------------------------------------
  const cookieWishlist = sanitizeIds(parse(readCookie(COOKIE_NAME)).map(String));
  const customerWishlist = getCustomerWishlist();
  let wishlist = getCustomerId()
    ? mergeWishlists(customerWishlist, cookieWishlist)
    : cookieWishlist;

  // --- UI : badge ---------------------------------------------------------
  const updateBadge = () => {
    const badge = document.querySelector('.wishlist-count');
    if (!badge) return;
    const n = wishlist.length;
    badge.textContent = n;
    badge.classList.toggle('opacity-0', n === 0);
    badge.classList.toggle('opacity-100', n > 0);
  };

  // --- Persistance (cookie uniquement) ------------------------------------
  const saveWishlist = async () => {
    wishlist = sanitizeIds(wishlist);
    writeCookie(COOKIE_NAME, stringify(wishlist));
  };

  // --- Sync serveur (App Proxy) ---------------------------------------------
  async function sendWishlistDelta(variantId, isAdd) {
    const cid = getCustomerId();
    if (!cid) {
      return { ok: false, connected: false, reason: 'not_logged_in' };
    }
    const id = String(variantId || '').trim();
    if (!/^\d+$/.test(id)) {
      return { ok: false, reason: 'invalid_variant_id' };
    }

    const payload = {
      customerID: String(cid),
      variantID: id,
      action: isAdd ? 'add' : 'delete'
    };

    const res = await (_fetch || fetch)('/apps/wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Wishlist proxy HTTP ${res.status} — ${text || 'no body'}`);
    }
    return res.json().catch(() => ({}));
  }

  async function syncWishlistState(nextWishlist) {
    const cid = getCustomerId();
    if (!cid) {
      return { ok: false, connected: false, reason: 'not_logged_in' };
    }

    const payload = {
      customerID: String(cid),
      action: 'sync',
      variantIDs: sanitizeIds(nextWishlist)
    };

    const res = await (_fetch || fetch)('/apps/wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Wishlist proxy HTTP ${res.status} — ${text || 'no body'}`);
    }
    return res.json().catch(() => ({}));
  }

  // --- UI : boutons (état cœur) ------------------------------------------
  const updateButtons = () => {
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      const id = String(btn.dataset.variantId || '');
      const isActive = wishlist.includes(id);

      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));

      const icon = btn.querySelector('.material-symbols-outlined, .material-icons');
      if (icon) {
        icon.textContent = isActive ? 'favorite' : 'favorite_border';
        icon.classList.toggle('text-red-600', isActive);
      }
    });
  };

  // --- État vide ----------------------------------------------------------
  const updateEmptyState = () => {
    const emptyText = document.querySelector('.wishlist-empty');
    const container = document.querySelector('.wishlist-items');
    if (!emptyText || !container) return;

    const visibleCards = container.querySelectorAll('.wishlist-card');
    const isEmpty = visibleCards.length === 0;

    // Supporte deux conventions possibles
    emptyText.classList.toggle('hidden-wishlist-text', !isEmpty);
    emptyText.classList.toggle('hidden', !isEmpty);
  };

  // --- Suppression d’une carte affichée si présente ----------------------
  const removeCardIfOnPage = (variantId) => {
    const card = document.querySelector(
      `.wishlist-card [data-variant-id="${variantId}"]`
    )?.closest('.wishlist-card');
    if (card) card.remove();
  };

  // --- Toggle d’un variant ------------------------------------------------
  const toggleItem = (variantId) => {
    const id = String(variantId || '');
    if (!isValidVariantId(id)) return;

    const i = wishlist.indexOf(id);
    const isAdd = i === -1;

    if (isAdd) wishlist.push(id);
    else wishlist.splice(i, 1);

    saveWishlist().then(updateBadge);
    updateButtons();
    removeCardIfOnPage(id);
    setTimeout(updateEmptyState, 0);

    // --- Appel serveur (sync App Proxy) ---
    // Non bloquant (on ignore les erreurs réseau pour garder l’UX fluide)
    sendWishlistDelta(id, isAdd).catch(err => {
      console.warn('Wishlist sync serveur échouée:', err.message);
    });
  };

  // --- Rendu d’une carte depuis le template (DOM sûr) --------------------
  const renderWishlistCard = (variant) => {
    if (!variant || !variant.product) return null;

    const tpl = document.getElementById('wishlist-product-card-template');
    if (!tpl || !tpl.content) {
      console.error('Wishlist – template introuvable: #wishlist-product-card-template');
      return null;
    }

    const node = tpl.content.firstElementChild.cloneNode(true);

    // Données avec fallback propres
    const vId = (variant.id || '').split('/').pop();
    const p = variant.product;
    const productTitle = p.title || '';
    const vTitle = variant.title && variant.title !== 'Default Title' ? ` - ${variant.title}` : '';
    const title = `${productTitle}${vTitle}`;

    const handle = p.handle || '';
    const url = handle ? `/products/${handle}` : '#';

    const image = (variant.image && variant.image.url) || (p.featuredImage && p.featuredImage.url) || '';

    const amount = (variant.price && variant.price.amount)
                || (p.priceRange && p.priceRange.minVariantPrice && p.priceRange.minVariantPrice.amount)
                || '';
    const currency = (variant.price && variant.price.currencyCode)
                  || (p.priceRange && p.priceRange.minVariantPrice && p.priceRange.minVariantPrice.currencyCode)
                  || 'EUR';

    const vendor = p.vendor || '';

    // Remplissage DOM (textContent / attributs uniquement)
    const linkImg = node.querySelector('[data-field="url"]');
    if (linkImg) linkImg.setAttribute('href', url);

    const imgEl = node.querySelector('[data-field="image"]');
    if (imgEl) {
      imgEl.setAttribute('src', image || '');
      imgEl.setAttribute('alt', title);
      imgEl.setAttribute('loading', 'lazy');
      imgEl.setAttribute('decoding', 'async');
    }

    const titleEl = node.querySelector('[data-field="title"]');
    if (titleEl) {
      titleEl.textContent = title;
      titleEl.setAttribute('href', url);
    }

    const vendorEl = node.querySelector('[data-field="vendor"]');
    if (vendorEl) vendorEl.textContent = vendor;

    const priceEl = node.querySelector('[data-field="price"]');
    if (priceEl) priceEl.textContent = fmtPrice(amount, currency);

    const btn = node.querySelector('[data-field="variant-id"]');
    if (btn) btn.setAttribute('data-variant-id', vId);

    return node;
  };

  // --- Rendu de la page wishlist -----------------------------------------
  const renderWishlistPage = async () => {
  const container = document.querySelector('.wishlist-items');
  const emptyText = document.querySelector('.wishlist-empty');
  if (!container) return;

  // Reset
  container.innerHTML = '';

  // 1) Aucune ID locale → affiche l’état vide
  if (!wishlist.length) {
    if (emptyText) emptyText.classList.remove('hidden', 'hidden-wishlist-text');
    updateEmptyState();
    return;
  }

  // 2) Token Storefront absent → ne bloque pas, affiche l’état vide
  if (!window.STOREFRONT_TOKEN) {
    console.warn('Wishlist: STOREFRONT_TOKEN manquant – rendu vide');
    if (emptyText) emptyText.classList.remove('hidden', 'hidden-wishlist-text');
    updateEmptyState();
    return;
  }

  const variantGIDs = wishlist.map(id => `gid://shopify/ProductVariant/${id}`);

  const query = `
    query getWishlistProducts($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          title
          image { url }
          price { amount currencyCode }
          product {
            handle
            title
            vendor
            featuredImage { url }
          }
        }
      }
    }`;

  let nodes = [];
  try {
    const res = await fetch('/api/2025-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query, variables: { ids: variantGIDs } })
    });
    const result = await res.json();
    nodes = result.data?.nodes || [];
  } catch (err) {
    console.error('Erreur chargement wishlist (réseau/API):', err);
  }

  const validNodes = (nodes || []).filter(Boolean);

  // 3) Si l’API ne retourne rien → montre l’état vide AU LIEU de ne rien rendre
  if (validNodes.length === 0) {
    if (emptyText) emptyText.classList.remove('hidden', 'hidden-wishlist-text');
    updateEmptyState();
    return;
  }

  const foundIds = new Set(validNodes.map(v => String(v.id.split('/').pop())));
  validNodes.forEach(variant => {
    const card = renderWishlistCard(variant);
    if (card) container.appendChild(card);
  });

  // Purge douce
  const before = wishlist.slice();
  const next = wishlist.filter(id => foundIds.has(id));
  if (next.length !== before.length) {
    const purged = before.filter(id => !foundIds.has(id));
    console.info('Wishlist – purge confirmée par API (variants inexistants):', purged);
    wishlist = next;
    await saveWishlist();
    updateBadge();
    if (getCustomerId()) {
      syncWishlistState(wishlist).catch(err => {
        console.warn('Wishlist sync purge échouée:', err.message);
      });
    }
  }

  updateButtons();
  setTimeout(updateEmptyState, 0);
};

  // --- Init DOMReady ------------------------------------------------------
  document.addEventListener('DOMContentLoaded', async () => {
    // Nettoyage immédiat d’anciennes valeurs corrompues
    const cleaned = sanitizeIds(wishlist);
    if (!sameWishlist(cleaned, wishlist)) {
      wishlist = cleaned;
    }

    await saveWishlist();

    if (getCustomerId() && !sameWishlist(wishlist, customerWishlist)) {
      syncWishlistState(wishlist).catch(err => {
        console.warn('Wishlist sync initiale échouée:', err.message);
      });
    }

    updateButtons();
    updateBadge();

    if (document.querySelector('.wishlist-items')) {
      renderWishlistPage();
    }
  });

  // --- Listeners globaux --------------------------------------------------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.wishlist-btn');
    if (btn) toggleItem(btn.dataset.variantId);
  });
})();
